import { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, PenLine, Upload, Trash2, Lock, CalendarClock, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "./ui/alert-dialog";
import { api, errMsg, fmtDate, fmtDateTime, openFile, download } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useApi, Panel, Empty, StatusChip, Field, btn, inputCls } from "./kit";

export const ORDER = ["job_description", "contract", "benefits", "performance_review", "growth", "other"];

function SignBox({ d, reload }) {
  const { user } = useAuth();
  const [checked, setChecked] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/documents/${d.id}/acknowledge`, { checked, typed_name: name }); toast.success("Signed. It's recorded in your file."); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-3 border-t pt-3" data-testid={`doc-sign-${d.id}`}>
      <label className="flex items-start gap-3 text-[15px]">
        <input type="checkbox" checked={checked} disabled={!d.opened} onChange={(e) => setChecked(e.target.checked)} className="mt-1 h-5 w-5 accent-[color:var(--dcbc-red)]" data-testid={`doc-sign-check-${d.id}`} />
        <span>I have read and understood “{d.title}”.</span>
      </label>
      <div><label htmlFor={`dn-${d.id}`} className="text-sm font-semibold">Type your full name: <span className="font-normal text-muted-foreground">{user.name}</span></label>
        <input id={`dn-${d.id}`} className={inputCls} value={name} disabled={!d.opened} onChange={(e) => setName(e.target.value)} data-testid={`doc-sign-name-${d.id}`} /></div>
      {!d.opened && <p className="text-sm text-amber-800">Open the document first to unlock signing.</p>}
      <button className={btn.primary} disabled={busy || !checked || !name || !d.opened} onClick={submit} data-testid={`doc-sign-submit-${d.id}`}><PenLine className="h-4 w-4" /> Sign</button>
    </div>
  );
}

function ReviewDate({ d, reload }) {
  const [v, setV] = useState(d.review_date || "");
  const save = async () => { if (v === (d.review_date || "")) return; try { await api.patch(`/documents/${d.id}`, { review_date: v || null }); toast.success(v ? "Review date set." : "Review date cleared."); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return <label className="inline-flex items-center gap-2 text-sm"><CalendarClock className="h-4 w-4" /> Review by
    <input type="date" className="rounded-md border px-2 py-1" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} data-testid={`doc-review-date-${d.id}`} /></label>;
}

function RemoveDoc({ d, reload }) {
  const del = async () => { try { await api.delete(`/documents/${d.id}`); toast.success("Document removed."); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild><button className="p-2 text-rose-700" aria-label="Remove document" data-testid={`doc-remove-${d.id}`}><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
      <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Remove “{d.title}”?</AlertDialogTitle>
        <AlertDialogDescription>It disappears from the file. Any signatures stay in the audit log.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Keep it</AlertDialogCancel><AlertDialogAction className="bg-rose-700 hover:bg-rose-800" onClick={del} data-testid={`doc-remove-confirm-${d.id}`}>Remove</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DocRow({ d, self, manage, reload }) {
  const open = () => openFile(d.file_id).then(() => setTimeout(reload, 600)).catch((e) => toast.error(errMsg(e)));
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Toronto" });
  return (
    <li id={`doc-${d.id}`} className="scroll-mt-24 rounded-xl border bg-white p-4" data-testid={`doc-${d.id}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex-1 min-w-0">
          <p className="flex flex-wrap items-center gap-2 font-semibold">{d.title}
            {d.managers_only && <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-bold text-white" data-testid={`doc-private-${d.id}`}><Lock className="h-3 w-3" /> Managers only</span>}
            {d.scope === "shared" && <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-bold text-sky-900">Shared with staff</span>}
            {d.requires_ack && (d.acknowledged ? <StatusChip status="completed" label={`Signed ${fmtDate(d.acknowledged.at)}`} /> : <StatusChip status="not_started" label="Signature needed" />)}
            {d.review_date && <StatusChip status={d.review_date < today ? "overdue" : "expiring_soon"} label={`Review by ${fmtDate(d.review_date)}`} />}
          </p>
          <p className="text-sm text-muted-foreground">Added {fmtDate(d.created_at)} by {d.uploaded_by_name}{d.note ? ` · ${d.note}` : ""}</p>
          {d.acknowledged && <p className="text-xs text-muted-foreground">Signed {fmtDateTime(d.acknowledged.at)} as “{d.acknowledged.typed_name}”</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {manage && d.scope === "person" && <ReviewDate d={d} reload={reload} />}
          <button className={btn.ghost} onClick={open} data-testid={`doc-open-${d.id}`}><FileText className="h-4 w-4" /> Open</button>
          {d.original_file_id && <button className="text-sm font-semibold underline" onClick={() => download(`/files/${d.original_file_id}`, d.title + ".docx").catch((e) => toast.error(errMsg(e)))} data-testid={`doc-original-${d.id}`}>Word file</button>}
          {manage && d.scope === "person" && <RemoveDoc d={d} reload={reload} />}
        </div>
      </div>
      {self && d.requires_ack && !d.acknowledged && <SignBox d={d} reload={reload} />}
    </li>
  );
}

export function DocList({ docs, self, manage, reload, cats }) {
  if (!docs.length) return <Empty testid="docs-empty">No documents yet.</Empty>;
  const label = Object.fromEntries((cats || []).map((c) => [c.id, c.label]));
  return (
    <div className="space-y-6">{ORDER.filter((k) => docs.some((d) => d.category === k)).map((k) => (
      <section key={k} data-testid={`docs-cat-${k}`}>
        <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">{label[k] || docs.find((d) => d.category === k).category_label}</h3>
        <ul className="space-y-2">{docs.filter((d) => d.category === k).map((d) => <DocRow key={d.id} d={d} self={self} manage={manage} reload={reload} />)}</ul>
      </section>))}</div>
  );
}

export function UploadDoc({ uid, onDone }) {
  const cats = useApi("/documents/categories");
  const blank = { title: "", category: "job_description", note: "", managers_only: false, requires_ack: false, review_date: "" };
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file to upload.");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(f).forEach(([k, v]) => fd.append(k, String(v)));
    try { await api.post(`/people/${uid}/documents`, fd); toast.success(f.managers_only ? "Added (managers only)." : "Added. The employee has been emailed."); setOpen(false); setF(blank); setFile(null); onDone(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className={btn.dark} data-testid="doc-upload-open"><Upload className="h-4 w-4" /> Add document</button></DialogTrigger>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Add to employee file</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="doc-upload-form">
          <Field label="Type" htmlFor="dc"><select id="dc" className={inputCls} value={f.category} onChange={set("category")} data-testid="doc-upload-category">
            {(cats.data || []).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}</select></Field>
          <Field label="Title" htmlFor="dt"><input id="dt" required className={inputCls} value={f.title} onChange={set("title")} placeholder="e.g. 2026 annual performance review" data-testid="doc-upload-title" /></Field>
          <Field label="File" htmlFor="df" hint="PDF, Word (.docx), JPG, PNG or HEIC, up to 20 MB. Word files are turned into a PDF."><input id="df" type="file" accept=".pdf,.docx,.jpg,.jpeg,.png,.heic,.heif" className={inputCls} onChange={(e) => setFile(e.target.files[0])} data-testid="doc-upload-file" /></Field>
          <Field label="Note (optional)" htmlFor="dno"><input id="dno" className={inputCls} value={f.note} onChange={set("note")} data-testid="doc-upload-note" /></Field>
          <Field label="Review by (optional)" htmlFor="drd" hint="You'll get a reminder 2 weeks before, e.g. next performance review."><input id="drd" type="date" className={inputCls} value={f.review_date} onChange={set("review_date")} data-testid="doc-upload-review-date" /></Field>
          <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" className="h-5 w-5" checked={f.requires_ack} disabled={f.managers_only} onChange={set("requires_ack")} data-testid="doc-upload-requires-ack" /> Employee must read and sign it</label>
          <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" className="h-5 w-5" checked={f.managers_only} onChange={(e) => setF({ ...f, managers_only: e.target.checked, requires_ack: e.target.checked ? false : f.requires_ack })} data-testid="doc-upload-managers-only" /> Managers only (hidden from the employee)</label>
          <button className={`${btn.primary} w-full`} disabled={busy} data-testid="doc-upload-submit">{busy ? "Uploading…" : "Upload"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function PersonDocuments({ uid }) {
  const { data, reload } = useApi(`/people/${uid}/documents`);
  const cats = useApi("/documents/categories");
  if (!data) return null;
  return (
    <Panel title={<span className="flex items-center gap-2"><FolderOpen className="h-5 w-5" /> Employee file</span>} action={data.can_upload && <UploadDoc uid={uid} onDone={reload} />} className="mt-6" testid="person-documents">
      <DocList docs={data.documents} manage={data.can_upload} reload={reload} cats={cats.data} />
    </Panel>
  );
}

export function HomeDocuments() {
  const { data } = useApi("/my/documents");
  const todo = (data?.documents || []).filter((d) => d.requires_ack && !d.acknowledged);
  if (!todo.length) return null;
  return (
    <Panel title="Documents to sign" testid="home-documents">
      <ul className="divide-y">{todo.map((d) => (
        <li key={d.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
          <span className="flex items-center gap-3"><FileText className="h-5 w-5 text-[color:var(--dcbc-red)]" /><span className="font-medium">{d.title}<span className="block text-sm font-normal text-muted-foreground">{d.category_label}</span></span></span>
          <Link to={`/records?tab=documents#doc-${d.id}`} className={btn.dark} data-testid={`home-doc-${d.id}`}>Read & sign</Link>
        </li>))}</ul>
    </Panel>
  );
}
